import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    Container, Row, Col, Card, CardBody,
    Form, FormGroup, Label, Input, Button, Spinner, Alert
} from "reactstrap";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { subscribeToPublicSongFlowGeneral } from "../../components/helpers/PublicCuratedSongFlowHelpers";

function PublicSongFlowSubscribeGeneral() {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [honeypot, setHoneypot] = useState("");
    const [emailError, setEmailError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const validateEmail = (value) => {
        if (!value || !value.trim()) return "Email address is required.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return "Please enter a valid email address.";
        return "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (honeypot) return;
        const err = validateEmail(email);
        if (err) { setEmailError(err); return; }
        setEmailError("");
        setIsSubmitting(true);
        setSubmitError("");

        const recaptchaToken = executeRecaptcha ? await executeRecaptcha("subscribe_song_flow") : null;
        const result = await subscribeToPublicSongFlowGeneral(email.trim(), name.trim() || null, recaptchaToken, honeypot);
        setIsSubmitting(false);

        if (result && result.success) {
            if (typeof window.gtag === "function") {
                window.gtag("event", "public_song_flow_subscribe_complete", { method: "general" });
            }
            setSubmitted(true);
        } else {
            setSubmitError("Something went wrong. Please try again.");
        }
    };

    return (
        <div className="position-relative">
            <section className="section section-lg section-shaped pb-250">
                <div className="shape shape-style-1 shape-default">
                    <span /><span /><span /><span /><span /><span /><span /><span /><span />
                </div>
                <Container className="py-lg-md d-flex">
                    <div className="col px-0">
                        <Row>
                            <Col lg="8">
                                <h1 className="display-3 text-white">Free curated song-flows</h1>
                                <p className="lead text-white">
                                    Sign up to receive hand-picked worship song-flows from The Worship Bible — free, no account needed.
                                </p>
                            </Col>
                        </Row>
                    </div>
                </Container>
                <div className="separator separator-bottom separator-skew">
                    <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                        <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                    </svg>
                </div>
            </section>

            <section className="section section-lg pt-lg-0 mt--200">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg="6">
                            <Card className="shadow border-0">
                                <CardBody className="py-5 px-5">
                                    {submitted ? (
                                        <div>
                                            <Alert color="success">
                                                <strong>You&rsquo;re subscribed!</strong> Check your inbox for a confirmation email.
                                            </Alert>
                                            <Link to="/" className="btn btn-primary mt-2">
                                                Back to home
                                            </Link>
                                        </div>
                                    ) : (
                                        <Form onSubmit={handleSubmit}>
                                            {/* Honeypot — hidden from real users, bots fill it in */}
                                            <div style={{ position: "absolute", left: "-9999px", top: "-9999px", opacity: 0, height: 0, overflow: "hidden" }} aria-hidden="true">
                                                <label htmlFor="website">Website</label>
                                                <Input id="website" type="text" name="website" value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex="-1" autoComplete="off" />
                                            </div>
                                            <p className="text-muted mb-4" style={{ fontSize: "0.9rem" }}>
                                                We send curated worship song-flows to help you plan your services.
                                                You can unsubscribe at any time.
                                            </p>
                                            <FormGroup>
                                                <Label for="name">Name <span className="text-muted" style={{ fontSize: "0.85rem" }}>(optional)</span></Label>
                                                <Input
                                                    id="name"
                                                    type="text"
                                                    placeholder="Your name"
                                                    value={name}
                                                    onChange={e => setName(e.target.value)}
                                                    disabled={isSubmitting}
                                                />
                                            </FormGroup>
                                            <FormGroup>
                                                <Label for="email">Email address <span className="text-danger">*</span></Label>
                                                <Input
                                                    id="email"
                                                    type="email"
                                                    placeholder="you@example.com"
                                                    value={email}
                                                    onChange={e => { setEmail(e.target.value); setEmailError(""); }}
                                                    invalid={!!emailError}
                                                    disabled={isSubmitting}
                                                />
                                                {emailError && (
                                                    <div className="invalid-feedback d-block">{emailError}</div>
                                                )}
                                            </FormGroup>

                                            {submitError && <Alert color="danger">{submitError}</Alert>}

                                            <Button color="primary" type="submit" disabled={isSubmitting} block>
                                                {isSubmitting ? <Spinner size="sm" /> : "Sign up free"}
                                            </Button>
                                        </Form>
                                    )}

                                    <hr className="my-4" />
                                    <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                                        Want to build your own worship song-flows?{" "}
                                        <Link to="/register/choose-your-plan">Register</Link> for a full account.
                                    </p>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
}

export default PublicSongFlowSubscribeGeneral;
